import { formatDistanceToNowStrict } from 'date-fns';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css, keyframes } from 'styled-components';

import {
  ErrorCloudIcon,
  IdleCloudIcon,
  SavedCloudIcon,
} from '../../svgs/CloudStatusIcons';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import { MODAL_TOOLTIP_STYLE } from './tooltipStyles';

import type { AutosaveStatus } from '../../hooks/useAutosave';

type Props = {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  /**
   * Stacking level for the details popover. It is portaled to `document.body`
   * (PLAT-11654: inside the clip modal the title row clips overflow, and the
   * modal's own stack sits above 1,000,000), so a host inside a high-z modal
   * passes its popover tier here. Defaults to a plain-page level.
   */
  popoverZIndex?: number;
  /**
   * Where the details popover hangs from the icon. `center` (default) for an
   * icon with room on both sides; `end` when the icon sits at the right edge
   * of its container (a modal header), so the popover opens leftward instead
   * of running off the viewport (PLAT-11649).
   */
  popoverAlign?: 'center' | 'end';
};

const DEFAULT_POPOVER_Z = 1000;
/** Gap between the icon and the popover below it. */
const POPOVER_OFFSET = 8;

/**
 * Persistent Google-Docs-style cloud icon that sits next to the document
 * title. Communicates save state at a glance and expands to a tooltip on
 * hover (mirroring "All changes saved to Drive").
 *
 * Four states:
 *   - saving → animated cloud with upload chevron
 *   - saved  → cloud with checkmark (the "you're safe" state)
 *   - error  → cloud with exclamation + error tint
 *   - idle   → outlined cloud; no edits yet or fresh surface
 *
 * For a more verbose inline status chip, use `AutosaveStatusIndicator`.
 */
export function AutosaveStatusIcon({
  status,
  lastSavedAt,
  popoverZIndex = DEFAULT_POPOVER_Z,
  popoverAlign = 'center',
}: Props) {
  // Stable tooltip id so multiple instances on one page don't collide.
  const idRef = useRef(
    `autosave-icon-${Math.random().toString(36).slice(2, 9)}`,
  );
  const tooltipId = idRef.current;

  // Tick once a minute so the "Saved · 2 minutes ago" string stays fresh
  // while the tooltip is visible without re-rendering the whole editor.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (status !== 'saved' || !lastSavedAt) return;
    const i = setInterval(() => forceTick(t => t + 1), 60_000);
    return () => clearInterval(i);
  }, [status, lastSavedAt]);

  const tooltip = buildTooltip(status, lastSavedAt);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closePopover = useCallback(() => setIsPopoverOpen(false), []);
  // The popover lives in a portal, so it is outside `wrapperRef` in the DOM:
  // a click inside it must not count as a click outside.
  const closeUnlessInPopover = useCallback(
    (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        popoverRef.current?.contains(event.target)
      )
        return;
      closePopover();
    },
    [closePopover],
  );
  useOutsideAlerter(wrapperRef, closeUnlessInPopover);

  // Popover placement — fixed, under the icon: centred on it, or with its
  // right edge on the icon's when `end`. Measured on open and again on
  // resize/scroll so it tracks the icon while it is visible.
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (!isPopoverOpen) {
      setPopoverPos(null);
      return;
    }
    const place = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPopoverPos({
        top: rect.bottom + POPOVER_OFFSET,
        left: popoverAlign === 'end' ? rect.right : rect.left + rect.width / 2,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isPopoverOpen, popoverAlign]);

  // Close on Escape so keyboard users can dismiss the popover.
  useEffect(() => {
    if (!isPopoverOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopover();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isPopoverOpen, closePopover]);

  // `saving` is represented by the inline spinner in AutosaveStatusIndicator —
  // we skip the cloud glyph here so the row never stacks two "activity" icons.
  // Only one save-status icon is ever visible at once.
  if (status === 'saving') return null;

  const popoverCopy = buildPopoverCopy(status, lastSavedAt);

  return (
    <Wrapper ref={wrapperRef}>
      <IconButton
        $status={status}
        type="button"
        // Tooltip stays on hover for quick context. We hide it while the
        // popover is open so the two don't collide over the same anchor.
        data-tooltip-id={isPopoverOpen ? undefined : tooltipId}
        data-tooltip-content={isPopoverOpen ? undefined : tooltip}
        aria-label={tooltip}
        aria-haspopup="dialog"
        aria-expanded={isPopoverOpen}
        onClick={() => setIsPopoverOpen(open => !open)}
        data-testid="autosave-status-icon"
      >
        {status === 'saved' && <SavedCloudIcon />}
        {status === 'error' && <ErrorCloudIcon />}
        {status === 'idle' && <IdleCloudIcon />}
      </IconButton>
      <Tooltip
        id={tooltipId}
        place="bottom"
        noArrow
        opacity={1}
        style={MODAL_TOOLTIP_STYLE}
      />
      {isPopoverOpen &&
        popoverPos &&
        createPortal(
          <Popover
            ref={popoverRef}
            role="dialog"
            aria-label="Autosave details"
            data-testid="autosave-status-popover"
            $align={popoverAlign}
            style={{
              top: popoverPos.top,
              left: popoverPos.left,
              zIndex: popoverZIndex,
            }}
          >
            <PopoverHeader $status={status}>
              <PopoverIcon aria-hidden="true">
                {status === 'saved' && <SavedCloudIcon />}
                {status === 'error' && <ErrorCloudIcon />}
                {status === 'idle' && <IdleCloudIcon />}
              </PopoverIcon>
              <PopoverTitle>{popoverCopy.title}</PopoverTitle>
            </PopoverHeader>
            <PopoverBody>{popoverCopy.body}</PopoverBody>
          </Popover>,
          document.body,
        )}
    </Wrapper>
  );
}

function buildPopoverCopy(
  status: AutosaveStatus,
  lastSavedAt: Date | null,
): { title: string; body: string } {
  switch (status) {
    case 'error':
      // Only what the code actually does: no host retries on a timer, watches
      // connectivity, or benefits from the user sitting still — the next save
      // attempt everywhere is the debounce fired by the next edit.
      return {
        title: "Couldn't save your last changes",
        body: "Your last saved version is safe. The changes since then are only in this tab — we'll try again the next time you make a change.",
      };
    case 'saved':
      return {
        title: 'All changes saved safely',
        body: lastSavedAt
          ? `Your work was saved ${formatDistanceToNowStrict(lastSavedAt, { addSuffix: true })}. You can leave this page at any time — nothing will be lost.`
          : 'Your work is saved. You can leave this page at any time — nothing will be lost.',
      };
    case 'idle':
    default:
      return {
        title: 'All changes saved safely',
        body: "We save automatically as you work, so you never need to click Save. If you close the tab, everything you've written stays exactly as you left it.",
      };
  }
}

function buildTooltip(
  status: AutosaveStatus,
  lastSavedAt: Date | null,
): string {
  switch (status) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return lastSavedAt
        ? `All changes saved · ${formatDistanceToNowStrict(lastSavedAt, { addSuffix: true })}`
        : 'All changes saved';
    case 'error':
      return "Couldn't save your last changes";
    case 'idle':
    default:
      return 'Changes are saved automatically';
  }
}

// --- Styling -------------------------------------------------------------

// Confirmed pop — fires when the saved state lands, so the check visually
// "ticks in" instead of just appearing.
const savedPop = keyframes`
  0%   { transform: scale(0.85); }
  60%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const IconButton = styled.button<{ $status: AutosaveStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  flex-shrink: 0;

  color: ${({ $status }) =>
    $status === 'error' ? 'var(--destructive)' : 'var(--text-muted)'};

  ${({ $status }) =>
    $status === 'saved'
      ? css`
          svg {
            animation: ${savedPop} 0.35s ease-out;
          }
        `
      : ''}

  &:hover {
    background: var(--surface-elevated);
    /* Stronger foreground on hover so the icon feels interactive —
       error keeps its destructive tint. */
    color: ${({ $status }) =>
      $status === 'error' ? 'var(--destructive)' : 'var(--foreground)'};
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

/* Each keyframe includes translateX(-50%) because CSS `transform` is a
   single property — a keyframe without it would replace the base rule mid-
   animation, snapping the popover off-center and then back on. */
const popoverIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -4px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

/* The end-aligned variant pulls itself fully left of the anchor point, which
   `place()` sets to the icon's right edge — same reason as above, each
   keyframe has to restate the translateX. */
const popoverInEnd = keyframes`
  from {
    opacity: 0;
    transform: translate(-100%, -4px);
  }
  to {
    opacity: 1;
    transform: translate(-100%, 0);
  }
`;

/* Portaled to document.body and placed with inline top/left/z-index, so it is
   never clipped by an `overflow: hidden` ancestor of the icon (the clip
   modal's title row, PLAT-11654) and can clear a modal's stacking context.
   `$align` decides which way it hangs off that point (PLAT-11649). */
const Popover = styled.div<{ $align: 'center' | 'end' }>`
  position: fixed;
  ${({ $align }) =>
    $align === 'end'
      ? css`
          transform: translateX(-100%);
          animation: ${popoverInEnd} 0.15s ease-out;
        `
      : css`
          transform: translateX(-50%);
          animation: ${popoverIn} 0.15s ease-out;
        `}
  min-width: 280px;
  max-width: 320px;
  padding: 14px 16px;
  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2);
`;

const PopoverHeader = styled.div<{ $status: AutosaveStatus }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: ${({ $status }) =>
    $status === 'error' ? 'var(--destructive)' : 'var(--foreground)'};
`;

const PopoverIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PopoverTitle = styled.h3`
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  line-height: 1.3;
`;

const PopoverBody = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  /* --text-secondary sits between --text-muted (too dark against the
     popover fill) and --foreground, giving readable contrast for the body. */
  color: var(--text-secondary);
  line-height: 1.5;
`;
